import {
	type MetaFunction,
	type HeadersFunction,
	type LinksFunction,
	type LoaderFunctionArgs,
} from '@remix-run/node'
import { Link, Outlet } from '@remix-run/react'
import { typedjson, useTypedLoaderData } from 'remix-typedjson'
import ArrowLink from '#app/components/arrowLink.tsx'
import PortfolioListingBlock from '#app/components/blocks/portfolioListingBlock.tsx'
import CallToActionCard from '#app/components/callToAction.tsx'
import Header from '#app/components/header.tsx'
import DownloadIcon from '#app/components/icons/download.tsx'
import Slider from '#app/components/slider.tsx'
import sliderCSS from '#app/styles/swiper.css?url'
import { CRAFT_QUERY_KEYS, getPageDetails } from '#app/utils/graphql.server.ts'
import { seomaticMeta } from '#app/utils/misc'
import { type SliderCard } from '../../../types/graphql.js'
import {
	type PortfolioListItem,
	type TextImageOverlayBlock as TextImageOverlayBlockType,
} from '../../../types/graphql.page.ts'
import { type RouteHandle } from '../../../types/index.ts'

export const handle: RouteHandle & {
	id: string
} = {
	id: 'portfolio-entry',
	restoreScroll: true,
}

export const links: LinksFunction = () => {
	return [{ rel: 'stylesheet', href: sliderCSS }]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const url = new URL(request.url)
	const noCache = Object.entries(Object.fromEntries(url.searchParams)).filter(
		([key]) => CRAFT_QUERY_KEYS.includes(key),
	).length
	const data = await getPageDetails(request, 'portfolio-entry')

	const portfolio = data.portfolioEntry && data.portfolioEntry[0]

	const textOverlayBlock: TextImageOverlayBlockType = {
		__typename: 'contentBlocks_textImageOverlay_BlockType',
		image: portfolio?.introImage || [],
		format: [{ styles: ['wave-texture-long'], imagePosition: 'right' }],
		link: {
			ariaLabel: '',
			customText: '',
			target: '',
			text: '',
			title: '',
			type: 'entry',
			url: '',
		},
		sortOrder: 0,
		subheader: '',
		text: portfolio?.introDescription || '',
		title: '',
		uid: '',
	}

	return typedjson(
		{
			hasNews: data.newsList.some((n) =>
				n.newsPortfolio.map((p) => p.title).includes(portfolio?.title || ''),
			),
			uid: data.entry.uid,
			slug: portfolio?.title,
			seo: data.seo,
			subTitle: portfolio?.title || '',
			title: 'Portfolio',
			portfolioStatus: portfolio?.portfolioStatus,
			contact: {
				number: {
					...portfolio?.contactNumber,
					customText:
						portfolio?.contactNumber.customText ||
						(portfolio?.contactNumber.url
							? new URL(
									portfolio.contactNumber.url,
								).pathname.toLocaleLowerCase()
							: ''),
				},
				email: {
					...portfolio?.contactEmail,
					customText:
						portfolio?.contactEmail.customText ||
						(portfolio?.contactEmail.url
							? new URL(portfolio.contactEmail.url).pathname.toLocaleLowerCase()
							: ''),
				},
				address: portfolio?.contactAddress,
				website: {
					...portfolio?.contactWebsite,
					customText:
						portfolio?.contactWebsite.customText ||
						(portfolio?.contactWebsite.url
							? new URL(
									portfolio.contactWebsite.url,
								).hostname.toLocaleLowerCase()
							: ''),
				},
			},
			listing: {
				type: portfolio?.listingType,
				image: portfolio?.listingImage,
			},
			intro: textOverlayBlock,
			gallery: portfolio?.gallery.map<SliderCard>((image, idx) => ({
				image: [image],
				uid: image.uid,
				title: '',
				subtitle: '',
				uri: `gallery/${idx + 1}`,
				portfolioStatus: true,
			})),
			onesheet: portfolio?.oneSheet[0],
			footer: data.entry.footer && {
				...data.entry.footer[0],
				enabled: data.entry.footerEnabled,
			},
		},
		{
			headers: {
				'Cache-Control': noCache
					? 'max-age=0, s-maxage=0'
					: 'max-age=60, s-maxage=300',
			},
		},
	)
}

export let headers: HeadersFunction = ({ loaderHeaders }) => {
	return { 'Cache-Control': loaderHeaders.get('Cache-Control') || '' }
}

export const meta: MetaFunction<typeof loader> = ({ data }: { data: any }) => {
	return seomaticMeta(data.seo)
}

export default function PortfolioRoute() {
	const {
		title,
		subTitle,
		portfolioStatus,
		footer,
		contact,
		gallery,
		intro,
		onesheet,
		slug,
		hasNews,
	} = useTypedLoaderData<typeof loader>()

	return (
		<>
			<Header subtitle={subTitle} title={title} />
			<main
				className={`container flex flex-col place-items-center gap-10 gap-y-20 pb-20 md:grid md:grid-cols-12`}
			>
				<PortfolioListingBlock
					status={portfolioStatus || false}
					block={intro as TextImageOverlayBlockType}
				/>
			</main>
			<Slider cards={gallery as PortfolioListItem[]} />
			<Outlet />
			<div className="relative -top-10 -mb-10 bg-green-100/5 px-6 pb-10 pt-12">
				<div className="divide-copper-100 container flex flex-col divide-y pt-20 md:flex-row md:space-y-0 md:divide-x md:divide-y-0">
					<div className="w-full pb-4 md:w-1/2 md:pb-0 md:pr-20">
						<h4>
							Interested in learning more about {subTitle}?{' '}
							{onesheet?.url
								? 'Download our informational one-sheet.'
								: 'Contact us for more information.'}
						</h4>
						{onesheet?.url && (
							<Link
								to={onesheet?.url || ''}
								className="btn btn-primary mt-4 flex flex-row place-items-center space-x-2"
							>
								<div>Download</div> <DownloadIcon className="h-4 w-4" />
							</Link>
						)}
						{hasNews && (
							<>
								<hr className="bg-copper-100 my-11 h-0.5 w-2/3" />
								<h4 className="pb-3">
									Stay updated on the latest news about {subTitle}
								</h4>
								<ArrowLink
									text="Read related news"
									type={'entry'}
									url={`/news?companies=${slug}`}
								/>
							</>
						)}
					</div>
					<div className="flex w-full flex-col space-y-4 pt-10 md:w-1/2 md:pl-32 md:pt-0">
						<h4>Contact {title}</h4>
						<div className="flex flex-col space-y-5">
							{contact.number.customText && (
								<a
									href={`${contact.number.url}`}
									className="flex flex-row space-x-5"
								>
									<img
										src="/assets/tel.svg"
										alt="tel"
										className="h-fit w-5 pt-2"
									/>
									<p>{contact.number.customText}</p>
								</a>
							)}
							{contact.email.customText && (
								<a
									href={`${contact.email.url}`}
									className="flex flex-row space-x-5"
								>
									<img
										src="/assets/email.svg"
										alt="email"
										className="h-fit w-5 pt-2"
									/>
									<p>{contact.email.customText}</p>
								</a>
							)}
							{contact.website.customText && (
								<a
									href={contact.website.url}
									className="flex flex-row space-x-5"
								>
									<img
										src="/assets/website.svg"
										alt="website"
										className="h-fit w-5 pt-2"
									/>
									<p>{contact.website.customText}</p>
								</a>
							)}
							{contact.address && (
								<div className="flex flex-row place-items-start space-x-5">
									<img
										src="/assets/address.svg"
										alt="address"
										className="h-fit w-5 pt-2"
									/>
									<div
										dangerouslySetInnerHTML={{ __html: contact.address }}
									></div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{footer && footer.enabled && footer.link ? (
				<CallToActionCard
					description={footer.description || ''}
					title={footer.title || ''}
					link={footer.link}
				/>
			) : null}
		</>
	)
}
